import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Modal, Select, MenuItem, FormControl, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import GlassDatePicker from './GlassDatePicker';

export interface TimeValue { h: number | null; m: number | null; s: number | null; }

// Select the whole box on the next frame — doing it inline loses to the browser
// placing the caret on mouseup and to React restoring the selection after a commit.
const selectAll = (el: HTMLInputElement | HTMLTextAreaElement) => requestAnimationFrame(() => el.select());

// One HH / MM / SS box. While focused it shows exactly what is being typed —
// zero-padding only on blur, otherwise the padded "01" eats the 2-char budget
// and the second digit can never be entered.
const TimeField: React.FC<{
  value: number | null; max: number; placeholder: string;
  onChange: (v: number | null) => void; sx: object;
}> = ({ value, max, placeholder, onChange, sx }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const padded = value === null ? '' : String(value).padStart(2, '0');
  return (
    <TextField size="small" type="text" inputMode="numeric" placeholder={placeholder}
      value={draft !== null ? draft : padded}
      onFocus={e => { setDraft(padded); selectAll(e.currentTarget); }}
      onBlur={() => setDraft(null)}
      onChange={e => {
        const raw = e.target.value.replace(/\D/g, '');
        // Keep the last two digits typed, so a third keystroke shifts instead of being swallowed;
        // if those two overflow (e.g. "70" in seconds), restart from the digit just typed
        let digits = raw.slice(-2);
        if (digits !== '' && parseInt(digits, 10) > max) digits = raw.slice(-1);
        setDraft(digits);
        onChange(digits === '' ? null : parseInt(digits, 10));
      }}
      // Every click re-selects the box: a 2-digit field is always retyped, never edited mid-caret
      slotProps={{ htmlInput: { onMouseUp: (e: React.MouseEvent<HTMLInputElement>) => selectAll(e.currentTarget) } }}
      sx={sx} />
  );
};

const TimeInput: React.FC<{ value: TimeValue; onChange: (v: TimeValue) => void; hasError?: boolean }> = ({ value, onChange, hasError }) => {
  const borderColor = hasError ? 'rgba(248,113,113,.5)' : 'var(--input-border)';
  const fieldSx = {
    width: 52,
    '& .MuiOutlinedInput-root': {
      fontSize: 14, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace',
      color: 'var(--text-primary)', background: 'var(--input-bg)',
      '& fieldset': { borderColor },
      '&:hover fieldset': { borderColor: 'var(--glass-border-strong)' },
      '&.Mui-focused fieldset': { borderColor: 'var(--accent)' },
    },
    '& input': { textAlign: 'center', padding: '6px 4px' },
  };
  const dot = <Typography sx={{ color: 'var(--text-disabled)', fontWeight: 700, lineHeight: 1 }}>:</Typography>;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <TimeField value={value.h} max={23} placeholder="HH" sx={fieldSx} onChange={h => onChange({ ...value, h })} />
      {dot}
      <TimeField value={value.m} max={59} placeholder="MM" sx={fieldSx} onChange={m => onChange({ ...value, m })} />
      {dot}
      <TimeField value={value.s} max={59} placeholder="SS" sx={fieldSx} onChange={s => onChange({ ...value, s })} />
    </Box>
  );
};

// ── Interfaces ─────────────────────────────────────────────────────────────────
export interface ScheduleEventData {
  id?: number;
  name: string;
  eventType: 'content' | 'screen_off';
  playlistId: number | null;
  fileId?: number | null;
  color: string;
  startDatetime: string;
  endDatetime: string;
  repeatType: string;
  repeatEvery: number | null;
  repeatUnit: string | null;
  repeatUntilType: string | null;
  repeatUntilDate: string | null;
  repeatOccurrences: number | null;
  repeatDaysMask: number | null;
  repeatDayOfMonth: number | null;
  repeatWeekOfMonth: number | null;
  repeatWeekday: number | null;
  repeatMonth: number | null;
  repeatMonthlyMode: 'day' | 'weekday' | null;
  repeatAnnuallyMode: 'same' | 'date' | 'weekday' | null;
}

export interface ExistingSlot {
  eventId: number;
  startDatetime: string;
  endDatetime: string;
  name?: string;
}

interface Playlist { id: number; name: string; }
interface MediaFile { id: number; original_name: string; mime_type: string; }

interface Props {
  open: boolean;
  event: Partial<ScheduleEventData> | null;
  playlists: Playlist[];
  mediaFiles?: MediaFile[];
  onClose: () => void;
  onSave: (data: ScheduleEventData) => void;
  onDelete?: () => void;
  lockedDate?: string | null;
  existingSlots?: ExistingSlot[];
  companyTz?: string;
}

// ── Constants ──────────────────────────────────────────────────────────────────
const COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#ec4899'];

const DAY_CHIP_LABELS  = ['Mo','Tu','We','Th','Fr','Sa','Su'];
const DAY_BITS         = [1,2,4,8,16,32,64];
const DAY_FULL_LABELS  = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const MONTH_SHORT      = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_FULL       = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ORDINAL_LABELS   = ['First','Second','Third','Fourth','Last'];
const ORDINAL_VALUES   = [1,2,3,4,-1];

const ordSuffix = (n: number) => {
  if (n >= 11 && n <= 13) return 'th';
  switch (n % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
};

const ORDINAL_OPTS  = ORDINAL_LABELS.map((l, i) => ({ label: l, value: ORDINAL_VALUES[i] }));
const WEEKDAY_OPTS  = DAY_FULL_LABELS.map((l, i) => ({ label: l, value: i }));
const MONTH_OPTS    = MONTH_FULL.map((l, i) => ({ label: l, value: i }));
const DAY_N_OPTS    = [...Array.from({ length: 28 }, (_, i) => ({ label: `${i+1}${ordSuffix(i+1)}`, value: i+1 })), { label: 'Last day', value: -1 }];
const DAY_N_NO_LAST = DAY_N_OPTS.slice(0, 28);

// ── Helpers ────────────────────────────────────────────────────────────────────
const pad = (n: number) => String(n).padStart(2, '0');

// Decompose a UTC ISO into date+time parts in a given IANA timezone
const partsInTz = (iso: string, tz: string) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(new Date(iso));
  const g = (t: string) => parseInt(parts.find(p => p.type === t)?.value ?? '0');
  return { year: g('year'), month: g('month'), day: g('day'), h: g('hour'), m: g('minute'), s: g('second') };
};

const isoToDateStr = (iso: string, tz?: string): string => {
  if (!iso) return '';
  if (tz) {
    const { year, month, day } = partsInTz(iso, tz);
    return `${year}-${pad(month)}-${pad(day)}`;
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
};

const isoToTime = (iso: string, tz?: string): TimeValue => {
  if (!iso) return { h: null, m: null, s: null };
  if (tz) {
    const { h, m, s } = partsInTz(iso, tz);
    return { h, m, s };
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { h: null, m: null, s: null };
  return { h: d.getHours(), m: d.getMinutes(), s: d.getSeconds() };
};

// Convert user-entered date+time (interpreted as `tz` local time) to UTC ISO
const buildISO = (date: string, time: TimeValue, tz?: string): string | null => {
  if (!date || time.h === null || time.m === null || time.s === null) return null;
  if (tz) {
    // Treat input as tz local time → find UTC equivalent
    const approxUtc = new Date(`${date}T${pad(time.h)}:${pad(time.m)}:${pad(time.s)}Z`);
    const shown = partsInTz(approxUtc.toISOString(), tz);
    const shownMs = Date.UTC(shown.year, shown.month - 1, shown.day, shown.h, shown.m, shown.s);
    const desiredMs = Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10), time.h, time.m, time.s);
    return new Date(desiredMs + (approxUtc.getTime() - shownMs)).toISOString();
  }
  const d = new Date(`${date}T${pad(time.h)}:${pad(time.m)}:${pad(time.s)}`);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const fmtHM = (h: number | null, m: number | null) =>
  h === null || m === null ? '--:--' : `${pad(h)}:${pad(m)}`;

function checkOverlap(start: Date, end: Date, slots: ExistingSlot[], excludeId?: number): string | null {
  for (const s of slots) {
    if (s.eventId === excludeId) continue;
    const ss = new Date(s.startDatetime), se = new Date(s.endDatetime);
    if (start < se && end > ss)
      return `${fmtHM(ss.getHours(), ss.getMinutes())} – ${fmtHM(se.getHours(), se.getMinutes())}${s.name ? ' · ' + s.name : ''}`;
  }
  return null;
}

// ── MUI Menu shared props ──────────────────────────────────────────────────────
const menuPaper = { bgcolor:'var(--glass-bg)', border:'1px solid var(--glass-border)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', color:'var(--text-primary)' };
const MPROPS = { slotProps: { paper: { sx: menuPaper } } };

// ── Styles ─────────────────────────────────────────────────────────────────────
const inpSx = {
  '& .MuiOutlinedInput-root': {
    fontSize:13, color:'var(--text-primary)', background:'var(--input-bg)',
    '& fieldset': { borderColor:'var(--input-border)' },
    '&:hover fieldset': { borderColor:'var(--glass-border-strong)' },
    '&.Mui-focused fieldset': { borderColor:'var(--accent)' },
  },
  '& .MuiSelect-icon': { color:'var(--text-disabled)' },
};

const smallSelSx = {
  '& .MuiOutlinedInput-root': {
    fontSize:12, color:'var(--text-primary)', background:'var(--input-bg)',
    '& fieldset': { borderColor:'var(--input-border)' },
    '&.Mui-focused fieldset': { borderColor:'var(--accent)' },
  },
  '& .MuiSelect-icon': { color:'var(--text-disabled)', fontSize:14 },
};

// V2 Flat Flow: sections separated by dividers, no card backgrounds
const secSx  = { borderBottom:'1px solid var(--divider)', pb:2, mb:2 };
const titleSx = { fontSize:10, fontWeight:800, textTransform:'uppercase' as const, letterSpacing:'.12em', color:'var(--accent-light)', mb:1.5 };
const rowSx   = { display:'flex', alignItems:'center', gap:1.5, mb:1.5, flexWrap:'wrap' as const };
const lblSx   = { fontSize:13, color:'var(--text-secondary)', minWidth:{ xs:'100%', md:64 }, flexShrink:0 };
const btnSx   = { flex:1, py:1, border:'1px solid var(--glass-border)', borderRadius:'8px', background:'var(--glass-bg)', color:'var(--text-secondary)', fontSize:13, fontWeight:600, cursor:'pointer', transition:'all .15s', fontFamily:'inherit', '&:hover':{ background:'var(--glass-hover)', color:'var(--text-primary)' } };


const InlineSel: React.FC<{ value: number; onChange: (v: number) => void; options: { label: string; value: number }[]; w?: number|string; }> = ({ value, onChange, options, w }) => (
  <FormControl size="small" sx={{ ...smallSelSx, minWidth: w ?? 90 }}>
    <Select value={value} onChange={e => onChange(Number(e.target.value))} MenuProps={MPROPS}>
      {options.map(o => <MenuItem key={o.value} value={o.value} sx={{ fontSize:12, color:'var(--text-primary)' }}>{o.label}</MenuItem>)}
    </Select>
  </FormControl>
);

const RadioDot: React.FC<{ active: boolean; onClick: () => void; }> = ({ active, onClick }) => (
  <Box onClick={onClick} sx={{ width:16, height:16, borderRadius:'50%', border:`2px solid ${active?'var(--accent)':'var(--text-disabled)'}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, mt:'2px', transition:'border-color .12s' }}>
    {active && <Box sx={{ width:8, height:8, borderRadius:'50%', background:'var(--accent)' }} />}
  </Box>
);

// ── EventModal ─────────────────────────────────────────────────────────────────
const EventModal: React.FC<Props> = ({ open, event, playlists, mediaFiles = [], onClose, onSave, onDelete, lockedDate, existingSlots = [], companyTz }) => {
  const { t } = useTranslation();

  const [eventType, setEventType]       = useState<'content'|'screen_off'>('content');
  const [contentType, setContentType]   = useState<'playlist'|'file'>('playlist');
  const [playlistId, setPlaylistId]     = useState<number|null>(null);
  const [fileId, setFileId]             = useState<number|null>(null);
  const [color, setColor]               = useState(COLORS[0]);
  const [cpOpen, setCpOpen]         = useState(false);
  const cpRef = useRef<HTMLDivElement>(null);

  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState<TimeValue>({ h:null, m:null, s:null });
  const [endDate, setEndDate]     = useState('');
  const [endTime, setEndTime]     = useState<TimeValue>({ h:null, m:null, s:null });

  const [repeatType, setRepeatType]                 = useState('none');
  const [repeatEvery, setRepeatEvery]               = useState(1);
  const [repeatDaysMask, setRepeatDaysMask]         = useState(0b0011111);
  const [monthlyMode, setMonthlyMode]               = useState<'day'|'weekday'>('day');
  const [repeatDayOfMonth, setRepeatDayOfMonth]     = useState(1);
  const [repeatWeekOfMonth, setRepeatWeekOfMonth]   = useState(1);
  const [repeatWeekday, setRepeatWeekday]           = useState(0);
  const [repeatMonth, setRepeatMonth]               = useState(0);
  const [annuallyMode, setAnnuallyMode]             = useState<'same'|'date'|'weekday'>('same');
  const [untilType, setUntilType]                   = useState('forever');
  const [untilDate, setUntilDate]                   = useState('');
  const [untilCount, setUntilCount]                 = useState(10);
  const [error, setError]                           = useState<string|null>(null);

  const isEdit   = !!event?.id;
  const isLocked = !!lockedDate && !isEdit;

  useEffect(() => {
    if (!open) return;
    setError(null); setCpOpen(false);
    const now = new Date();

    setEventType((event?.eventType as 'content'|'screen_off') ?? 'content');
    const hasFile = event?.fileId != null;
    setContentType(hasFile ? 'file' : 'playlist');
    setPlaylistId(event?.playlistId ?? null);
    setFileId(event?.fileId ?? null);
    setColor(event?.color ?? COLORS[0]);
    setRepeatType(event?.repeatType ?? 'none');
    setRepeatEvery(event?.repeatEvery ?? 1);
    setRepeatDaysMask(event?.repeatDaysMask ?? 0b0011111);
    setMonthlyMode(event?.repeatMonthlyMode ?? 'day');
    setRepeatDayOfMonth(event?.repeatDayOfMonth ?? now.getDate());
    setRepeatWeekOfMonth(event?.repeatWeekOfMonth ?? 1);
    setRepeatWeekday(event?.repeatWeekday ?? 0);
    setRepeatMonth(event?.repeatMonth ?? now.getMonth());
    setAnnuallyMode(event?.repeatAnnuallyMode ?? 'same');
    setUntilType(event?.repeatUntilType ?? 'forever');
    setUntilDate(event?.repeatUntilDate ? isoToDateStr(event.repeatUntilDate, companyTz) : '');
    setUntilCount(event?.repeatOccurrences ?? 10);

    if (event?.startDatetime) {
      setStartDate(isoToDateStr(event.startDatetime, companyTz));
      setStartTime(isoToTime(event.startDatetime, companyTz));
    } else {
      setStartDate(lockedDate ?? isoToDateStr(now.toISOString(), companyTz));
      const nowT = isoToTime(now.toISOString(), companyTz);
      setStartTime({ h: nowT.h, m: 0, s: 0 });
    }
    if (event?.endDatetime) {
      setEndDate(isoToDateStr(event.endDatetime, companyTz));
      setEndTime(isoToTime(event.endDatetime, companyTz));
    } else if (isLocked) {
      setEndDate(lockedDate!);
      setEndTime({ h:null, m:null, s:null });
    } else {
      const plusH = new Date(now.getTime() + 3600_000);
      setEndDate(isoToDateStr(plusH.toISOString(), companyTz));
      const plusT = isoToTime(plusH.toISOString(), companyTz);
      setEndTime({ h: plusT.h, m: 0, s: 0 });
    }
  }, [open, event, lockedDate, isLocked]);

  useEffect(() => {
    if (!cpOpen) return;
    const h = (e: MouseEvent) => { if (cpRef.current && !cpRef.current.contains(e.target as Node)) setCpOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [cpOpen]);

  const handleSave = () => {
    setError(null);
    const startISO = buildISO(startDate, startTime, companyTz);
    if (!startISO) { setError(t('schedules.startRequired')); return; }
    if (endTime.h === null || endTime.m === null || endTime.s === null) { setError(t('schedules.endRequired')); return; }
    const endISO = buildISO(endDate, endTime, companyTz);
    if (!endISO) { setError(t('schedules.endRequired')); return; }
    const start = new Date(startISO), end = new Date(endISO);
    if (end <= start) { setError(t('schedules.endBeforeStart')); return; }
    const conflict = checkOverlap(start, end, existingSlots, event?.id);
    if (conflict) { setError(`${t('schedules.overlapError')}: ${conflict}`); return; }
    if (eventType === 'content') {
      if (contentType === 'playlist' && !playlistId) { setError('Please select a playlist'); return; }
      if (contentType === 'file' && !fileId) { setError('Please select a file'); return; }
    }
    const untilISO = untilDate ? buildISO(untilDate, { h:23, m:59, s:59 }, companyTz) : null;
    const isWeekly = repeatType === 'weekly';
    const isMonthlyDay = repeatType === 'monthly' && monthlyMode === 'day';
    const isMonthlyWD  = repeatType === 'monthly' && monthlyMode === 'weekday';
    const isAnnuallyWD = repeatType === 'annually' && annuallyMode === 'weekday';
    const isAnnuallyDate = repeatType === 'annually' && annuallyMode === 'date';
    const selectedPlaylist = playlists.find(p => p.id === playlistId);
    const selectedFile = mediaFiles.find(f => f.id === fileId);
    const autoName = eventType === 'screen_off' ? 'Screen Off'
      : contentType === 'playlist' ? (selectedPlaylist?.name ?? 'Content')
      : (selectedFile?.original_name ?? 'File');
    onSave({
      id: event?.id,
      name: autoName,
      eventType,
      playlistId: eventType === 'content' && contentType === 'playlist' ? playlistId : null,
      fileId: eventType === 'content' && contentType === 'file' ? fileId : null,
      color, startDatetime: startISO, endDatetime: endISO,
      repeatType,
      repeatEvery:       repeatType !== 'none' ? repeatEvery : null,
      repeatUnit:        null,
      repeatUntilType:   repeatType !== 'none' ? untilType : null,
      repeatUntilDate:   repeatType !== 'none' && untilType === 'date' ? (untilISO ?? null) : null,
      repeatOccurrences: repeatType !== 'none' && untilType === 'occurrences' ? untilCount : null,
      repeatDaysMask:    isWeekly ? repeatDaysMask : null,
      repeatDayOfMonth:  isMonthlyDay || isAnnuallyDate ? repeatDayOfMonth : null,
      repeatWeekOfMonth: isMonthlyWD || isAnnuallyWD ? repeatWeekOfMonth : null,
      repeatWeekday:     isMonthlyWD || isAnnuallyWD ? repeatWeekday : null,
      repeatMonth:       isAnnuallyDate || isAnnuallyWD ? repeatMonth : null,
      repeatMonthlyMode: repeatType === 'monthly' ? monthlyMode : null,
      repeatAnnuallyMode: repeatType === 'annually' ? annuallyMode : null,
    });
  };

  // ── Repeat sub-section ─────────────────────────────────────────────────────
  const renderRepeatSub = () => {
    if (repeatType === 'none' || repeatType === 'weekday') return null;

    const unitLabel = { daily:'day(s)', weekly:'week(s)', monthly:'month(s)', annually:'year(s)' }[repeatType] ?? '';

    return (
      <Box sx={{ mt:1.5, pl:1.5, borderLeft:'2px solid var(--divider)' }}>
        {/* Every N [unit] */}
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1.5, flexWrap:'wrap' }}>
          <Typography sx={{ ...lblSx, minWidth:{ xs:'100%', md:52 } }}>Every</Typography>
          <Box sx={{ display:'flex', border:'1px solid var(--glass-border)', borderRadius:'7px', overflow:'hidden', flexShrink:0 }}>
            {[1, 2, 3].map(n => (
              <Box key={n} component="button" onClick={() => setRepeatEvery(n)}
                sx={{ px:'11px', py:'6px', fontSize:13, fontWeight:600, fontFamily:'inherit', cursor:'pointer', border:'none',
                  background: repeatEvery===n ? 'var(--selected-bg)' : 'transparent',
                  color: repeatEvery===n ? 'var(--text-primary)' : 'var(--text-secondary)',
                  transition:'all .12s', '&:hover': repeatEvery===n?{}:{ background:'var(--glass-hover)' } }}>
                {n}
              </Box>
            ))}
          </Box>
          <TextField type="number" size="small" value={repeatEvery}
            onChange={e => setRepeatEvery(Math.max(1, parseInt(e.target.value)||1))}
            slotProps={{ htmlInput:{ min:1, style:{ textAlign:'center', fontSize:13, width:34 } } }}
            sx={{ ...inpSx, width:64 }} />
          <Typography sx={{ fontSize:13, color:'var(--text-secondary)' }}>{unitLabel}</Typography>
        </Box>

        {/* Weekly: day chips */}
        {repeatType === 'weekly' && (
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <Typography sx={{ ...lblSx, minWidth:52 }}>On days</Typography>
            <Box sx={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
              {DAY_CHIP_LABELS.map((label, i) => {
                const on = !!(repeatDaysMask & DAY_BITS[i]);
                return (
                  <Box key={label} onClick={() => setRepeatDaysMask(m => m ^ DAY_BITS[i])}
                    sx={{ width:32, height:32, borderRadius:'50%', border:`1px solid ${on?'var(--accent)':'var(--input-border)'}`, background:on?'var(--accent)':'var(--input-bg)', color:on?'#fff':'var(--text-secondary)', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'all .14s', boxShadow:on?'0 0 8px var(--accent-glow)':'none', userSelect:'none' }}>
                    {label}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Monthly: day vs nth-weekday */}
        {repeatType === 'monthly' && (
          <Box sx={{ display:'flex', flexDirection:'column', gap:1.25 }}>
            <Box sx={{ display:'flex', alignItems:'flex-start', gap:1, cursor:'pointer' }} onClick={() => setMonthlyMode('day')}>
              <RadioDot active={monthlyMode==='day'} onClick={() => setMonthlyMode('day')} />
              <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
                <Typography sx={{ fontSize:13, color:'var(--text-primary)' }}>On the</Typography>
                <InlineSel value={repeatDayOfMonth} onChange={setRepeatDayOfMonth} options={DAY_N_OPTS} w={100} />
                <Typography sx={{ fontSize:13, color:'var(--text-primary)' }}>of the month</Typography>
              </Box>
            </Box>
            <Box sx={{ display:'flex', alignItems:'flex-start', gap:1, cursor:'pointer' }} onClick={() => setMonthlyMode('weekday')}>
              <RadioDot active={monthlyMode==='weekday'} onClick={() => setMonthlyMode('weekday')} />
              <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
                <Typography sx={{ fontSize:13, color:'var(--text-primary)' }}>On the</Typography>
                <InlineSel value={repeatWeekOfMonth} onChange={setRepeatWeekOfMonth} options={ORDINAL_OPTS} w={88} />
                <InlineSel value={repeatWeekday} onChange={setRepeatWeekday} options={WEEKDAY_OPTS} w={110} />
                <Typography sx={{ fontSize:13, color:'var(--text-primary)' }}>of the month</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Annually: same / specific date / nth weekday of month */}
        {repeatType === 'annually' && (
          <Box sx={{ display:'flex', flexDirection:'column', gap:1.25 }}>
            <Box sx={{ display:'flex', alignItems:'flex-start', gap:1, cursor:'pointer' }} onClick={() => setAnnuallyMode('same')}>
              <RadioDot active={annuallyMode==='same'} onClick={() => setAnnuallyMode('same')} />
              <Box sx={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                <Typography sx={{ fontSize:13, color:'var(--text-primary)' }}>Same date each year</Typography>
                {startDate && (
                  <Typography sx={{ fontSize:13, color:'var(--text-disabled)' }}>
                    ({MONTH_SHORT[new Date(startDate+'T00:00:00').getMonth()]} {new Date(startDate+'T00:00:00').getDate()})
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display:'flex', alignItems:'flex-start', gap:1, cursor:'pointer' }} onClick={() => setAnnuallyMode('date')}>
              <RadioDot active={annuallyMode==='date'} onClick={() => setAnnuallyMode('date')} />
              <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
                <InlineSel value={repeatMonth} onChange={setRepeatMonth} options={MONTH_OPTS} w={100} />
                <InlineSel value={repeatDayOfMonth} onChange={setRepeatDayOfMonth} options={DAY_N_NO_LAST} w={76} />
              </Box>
            </Box>
            <Box sx={{ display:'flex', alignItems:'flex-start', gap:1, cursor:'pointer' }} onClick={() => setAnnuallyMode('weekday')}>
              <RadioDot active={annuallyMode==='weekday'} onClick={() => setAnnuallyMode('weekday')} />
              <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
                <InlineSel value={repeatWeekOfMonth} onChange={setRepeatWeekOfMonth} options={ORDINAL_OPTS} w={88} />
                <InlineSel value={repeatWeekday} onChange={setRepeatWeekday} options={WEEKDAY_OPTS} w={110} />
                <Typography sx={{ fontSize:13, color:'var(--text-secondary)' }}>of</Typography>
                <InlineSel value={repeatMonth} onChange={setRepeatMonth} options={MONTH_OPTS} w={100} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // ── Stop repeat block ──────────────────────────────────────────────────────
  const renderStop = () => {
    if (repeatType === 'none') return null;
    return (
      <Box sx={{ mt:1.5, pl:1.5, borderLeft:'2px solid var(--divider)' }}>
        <Typography sx={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:'var(--text-disabled)', mb:1 }}>Stop repeating</Typography>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb: untilType==='forever'?0:1.25 }}>
          <Typography sx={{ ...lblSx, minWidth:52 }}>When</Typography>
          <FormControl fullWidth size="small" sx={inpSx}>
            <Select value={untilType} onChange={e => setUntilType(e.target.value)} MenuProps={MPROPS}>
              <MenuItem value="forever"     sx={{ fontSize:13 }}>Never — repeat forever</MenuItem>
              <MenuItem value="date"         sx={{ fontSize:13 }}>On a specific date</MenuItem>
              <MenuItem value="occurrences"  sx={{ fontSize:13 }}>After N repetitions</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {untilType === 'date' && (
          <>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
              <Typography sx={{ ...lblSx, minWidth:52 }}>Date</Typography>
              <GlassDatePicker value={untilDate} onChange={setUntilDate} />
              <Box sx={{ fontSize:10, color:'var(--text-disabled)', background:'var(--glass-bg)', border:'1px solid var(--glass-border)', borderRadius:'5px', px:'7px', py:'3px', whiteSpace:'nowrap', flexShrink:0 }}>date only</Box>
            </Box>
            <Typography sx={{ fontSize:11, color:'var(--text-disabled)', mt:0.75, ml:'64px', lineHeight:1.5 }}>
              ℹ Repetitions stop at the end of this day — no time is set here
            </Typography>
          </>
        )}
        {untilType === 'occurrences' && (
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <Typography sx={{ ...lblSx, minWidth:52 }}>Count</Typography>
            <TextField type="number" size="small" value={untilCount}
              onChange={e => setUntilCount(Math.max(1, parseInt(e.target.value)||1))}
              slotProps={{ htmlInput:{ min:1, style:{ textAlign:'center', fontSize:13 } } }} sx={{ ...inpSx, width:90 }} />
            <Typography sx={{ fontSize:13, color:'var(--text-secondary)' }}>repetitions</Typography>
          </Box>
        )}
      </Box>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ position:'absolute',
        top:{ xs:0, md:'50%' }, left:{ xs:0, md:'50%' }, transform:{ xs:'none', md:'translate(-50%,-50%)' },
        width:{ xs:'100%', md:520 }, height:{ xs:'100%', md:'auto' }, maxHeight:{ xs:'100%', md:'93vh' },
        display:{ xs:'flex', md:'block' }, flexDirection:'column',
        overflowY:{ xs:'hidden', md:'auto' },
        background:'var(--glass-bg)', backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)',
        border:{ xs:'none', md:'1px solid var(--glass-border-strong)' }, borderRadius:{ xs:0, md:'16px' },
        boxShadow:{ xs:'none', md:'0 32px 80px rgba(0,0,0,.5)' }, outline:'none',
        '&::-webkit-scrollbar':{ width:6 }, '&::-webkit-scrollbar-track':{ background:'transparent' }, '&::-webkit-scrollbar-thumb':{ background:'var(--glass-border)', borderRadius:3 } }}>

        {/* Header — ✕ top right; on mobile it stays put while the body scrolls */}
        <Box sx={{ display:'flex', alignItems:'center', gap:1, flexShrink:0, p:{ xs:'14px 16px', md:'18px 22px 14px' }, borderBottom:{ xs:'1px solid var(--divider)', md:'none' } }}>
          <Typography sx={{ fontSize:{ xs:16, md:17 }, fontWeight:700, flex:1, minWidth:0, color:'var(--text-primary)' }}>
            {isEdit ? t('schedules.editRecord') : t('schedules.newRecord')}
          </Typography>
          <Box onClick={onClose} sx={{ width:{ xs:32, md:28 }, height:{ xs:32, md:28 }, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'7px', cursor:'pointer', flexShrink:0, fontSize:{ xs:16, md:'inherit' }, border:{ xs:'1px solid var(--glass-border)', md:'none' }, background:{ xs:'var(--glass-bg)', md:'transparent' }, color:'var(--text-secondary)', '&:hover':{ background:'var(--glass-hover)', color:'var(--text-primary)' } }}>✕</Box>
        </Box>

        <Box sx={{ px:{ xs:'16px', md:'22px' }, pt:{ xs:'14px', md:'10px' }, pb:'6px', flex:{ xs:1, md:'none' }, minHeight:0, overflowY:{ xs:'auto', md:'visible' }, '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-thumb':{ background:'var(--glass-border)', borderRadius:2 } }}>

          {/* ── SETTINGS ──────────────────────────────────────────────── */}
          <Box sx={secSx}>
            <Typography sx={titleSx}>Settings</Typography>

            {/* Event type toggle */}
            <Box sx={{ ...rowSx, mb:1 }}>
              <Typography sx={lblSx}>Type</Typography>
              <Box sx={{ display:'flex', gap:1 }}>
                {(['content','screen_off'] as const).map(type => {
                  const isScreenOff = type === 'screen_off';
                  return (
                    <Box key={type} onClick={() => { if (!isScreenOff) setEventType(type); }} title={isScreenOff ? 'Coming soon' : undefined} sx={{ display:'flex', alignItems:'center', gap:'6px', px:'12px', py:'5px', borderRadius:'7px', cursor: isScreenOff ? 'not-allowed' : 'pointer', border:`1px solid ${eventType===type?'var(--selected-border)':'transparent'}`, background:eventType===type?'var(--selected-bg)':'transparent', color: isScreenOff ? 'var(--text-disabled)' : eventType===type?'var(--text-primary)':'var(--text-secondary)', fontSize:13, fontWeight:500, transition:'all .12s', userSelect:'none', opacity: isScreenOff ? 0.45 : 1, '&:hover': (!isScreenOff && eventType!==type)?{ background:'var(--glass-bg)' }:{} }}>
                      {type==='content'?'📺':'⬛'} {type==='content'?'Schedule Content':'Turn screen Off'}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box sx={{ height:'1px', background:'var(--divider)', mb:1.5 }} />

            {/* Content: type toggle + picker + color */}
            {eventType === 'content' && (
              <>
                {/* Row 1: label | [Playlist][File] toggles | color dot */}
                <Box sx={{ ...rowSx, mb: 1 }}>
                  <Typography sx={lblSx}>Content</Typography>
                  <Box sx={{ display:'flex', gap:'6px' }}>
                    {(['playlist', 'file'] as const).map(ct => (
                      <Box key={ct} onClick={() => setContentType(ct)}
                        sx={{ display:'flex', alignItems:'center', gap:'5px', px:'12px', py:'5px', borderRadius:'7px', cursor:'pointer', border:`1px solid ${contentType===ct?'var(--selected-border)':'var(--glass-border)'}`, background:contentType===ct?'var(--selected-bg)':'transparent', color:contentType===ct?'var(--text-primary)':'var(--text-secondary)', fontSize:13, fontWeight:500, transition:'all .12s', userSelect:'none', '&:hover':contentType!==ct?{ background:'var(--glass-hover)' }:{} }}>
                        {ct === 'playlist' ? '🎬' : '📁'} {ct === 'playlist' ? 'Playlist' : 'File'}
                      </Box>
                    ))}
                  </Box>
                  {/* Color picker at end of row */}
                  <Box ref={cpRef} sx={{ position:'relative', flexShrink:0, ml:'auto' }}>
                    <Box onClick={() => setCpOpen(o => !o)} sx={{ width:28, height:28, borderRadius:'7px', background:color, cursor:'pointer', border:'1px solid var(--glass-border)', transition:'transform .12s', '&:hover':{ transform:'scale(1.1)' } }} />
                    {cpOpen && (
                      <Box sx={{ position:'absolute', top:34, right:0, zIndex:1400, background:'var(--glass-bg)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', border:'1px solid var(--glass-border-strong)', borderRadius:'10px', p:'10px', display:'flex', gap:'7px', flexWrap:'wrap', width:172, boxShadow:'0 12px 32px rgba(0,0,0,.5)' }}>
                        {COLORS.map(c => (
                          <Box key={c} onClick={() => { setColor(c); setCpOpen(false); }} sx={{ width:22, height:22, borderRadius:'50%', background:c, cursor:'pointer', border:color===c?'2px solid rgba(255,255,255,.85)':'2px solid transparent', transform:color===c?'scale(1.2)':'scale(1)', boxShadow:color===c?'0 0 0 1px rgba(255,255,255,.2)':'none', transition:'all .12s', '&:hover':{ transform:'scale(1.12)' } }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
                {/* Row 2: indent + picker */}
                <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:1.5 }}>
                  <Box sx={{ display:{ xs:'none', md:'block' }, minWidth:64, flexShrink:0 }} />
                  <FormControl fullWidth size="small" sx={inpSx}>
                    {contentType === 'playlist' ? (
                      <Select value={playlistId!==null?String(playlistId):''} onChange={e => setPlaylistId(e.target.value===''?null:Number(e.target.value))} displayEmpty MenuProps={MPROPS}>
                        <MenuItem value=""><em style={{ color:'var(--text-disabled)', fontStyle:'normal' }}>Select playlist…</em></MenuItem>
                        {playlists.map(p => <MenuItem key={p.id} value={p.id} sx={{ fontSize:13 }}>{p.name}</MenuItem>)}
                      </Select>
                    ) : (
                      <Select value={fileId!==null?String(fileId):''} onChange={e => setFileId(e.target.value===''?null:Number(e.target.value))} displayEmpty MenuProps={MPROPS}>
                        <MenuItem value=""><em style={{ color:'var(--text-disabled)', fontStyle:'normal' }}>Select file…</em></MenuItem>
                        {mediaFiles.map(f => <MenuItem key={f.id} value={f.id} sx={{ fontSize:13 }}>{f.original_name}</MenuItem>)}
                      </Select>
                    )}
                  </FormControl>
                </Box>
              </>
            )}

            {/* Color for screen_off */}
            {eventType === 'screen_off' && (
              <Box sx={{ ...rowSx, mb:0 }}>
                <Typography sx={lblSx}>Color</Typography>
                <Box ref={cpRef} sx={{ position:'relative', flexShrink:0 }}>
                  <Box onClick={() => setCpOpen(o => !o)} sx={{ width:28, height:28, borderRadius:'7px', background:color, cursor:'pointer', border:'1px solid var(--glass-border)', transition:'transform .12s', '&:hover':{ transform:'scale(1.1)' } }} />
                  {cpOpen && (
                    <Box sx={{ position:'absolute', top:34, left:0, zIndex:1400, background:'var(--glass-bg)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', border:'1px solid var(--glass-border-strong)', borderRadius:'10px', p:'10px', display:'flex', gap:'7px', flexWrap:'wrap', width:172, boxShadow:'0 12px 32px rgba(0,0,0,.5)' }}>
                      {COLORS.map(c => (
                        <Box key={c} onClick={() => { setColor(c); setCpOpen(false); }} sx={{ width:22, height:22, borderRadius:'50%', background:c, cursor:'pointer', border:color===c?'2px solid rgba(255,255,255,.85)':'2px solid transparent', transform:color===c?'scale(1.2)':'scale(1)', boxShadow:color===c?'0 0 0 1px rgba(255,255,255,.2)':'none', transition:'all .12s', '&:hover':{ transform:'scale(1.12)' } }} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* ── REPEAT ────────────────────────────────────────────────── */}
          <Box sx={secSx}>
            <Typography sx={titleSx}>Repeat</Typography>
            <Box sx={rowSx}>
              <Typography sx={lblSx}>Type</Typography>
              <FormControl fullWidth size="small" sx={inpSx}>
                <Select value={repeatType} onChange={e => setRepeatType(e.target.value)} MenuProps={MPROPS}>
                  <MenuItem value="daily"   sx={{ fontSize:13 }}>Daily</MenuItem>
                  <MenuItem value="weekly"  sx={{ fontSize:13 }}>Weekly</MenuItem>
                  <MenuItem value="monthly" sx={{ fontSize:13 }}>Monthly</MenuItem>
                  <MenuItem value="annually"sx={{ fontSize:13 }}>Annually</MenuItem>
                  <MenuItem value="none"    sx={{ fontSize:13 }}>None (one-time)</MenuItem>
                  {/* Legacy: weekly with Mon–Fri covers it, kept only so old records still open */}
                  {repeatType === 'weekday' && <MenuItem value="weekday" sx={{ fontSize:13 }}>Every weekday (Mon–Fri)</MenuItem>}
                </Select>
              </FormControl>
            </Box>
            {renderRepeatSub()}
          </Box>
          {/* ── DATE & TIME ───────────────────────────────────────────── */}
          <Box sx={{ pb:2, mb:0 }}>
            <Box sx={{ display:'flex', alignItems:'center', mb:1.5 }}>
              <Typography sx={{ ...titleSx, mb:0 }}>Date &amp; Time</Typography>
              {isLocked && (
                <Box sx={{ ml:1.5, display:'inline-flex', alignItems:'center', gap:'4px', background:'var(--glass-bg)', border:'1px solid var(--glass-border)', borderRadius:'5px', px:'7px', py:'2px' }}>
                  <Box component="span" sx={{ fontSize:10 }}>🔒</Box>
                  <Typography sx={{ fontSize:10, color:'var(--text-disabled)' }}>Date locked</Typography>
                </Box>
              )}
            </Box>

            {/* Timezone hint */}
            {companyTz && (
              <Typography sx={{ fontSize:11, color:'var(--text-disabled)', mb:1.25, ml:'4px' }}>
                {t('schedules.timesInTz', { tz: companyTz })}
              </Typography>
            )}

            {/* Starts */}
            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:0.75, flexWrap:'wrap' }}>
              <Typography sx={{ ...lblSx, minWidth:{ xs:'100%', md:52 } }}>Starts</Typography>
              <GlassDatePicker value={startDate} onChange={setStartDate} disabled={isLocked} />
              <TimeInput value={startTime} onChange={setStartTime} />
            </Box>

            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:0.75 }}>
              <Box sx={{ display:{ xs:'none', md:'block' }, minWidth:52 }} />
              <Box sx={{ flex:1, height:'1px', background:'var(--glass-border)' }} />
            </Box>

            {/* Ends */}
            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:error?1.5:0, flexWrap:'wrap' }}>
              <Typography sx={{ ...lblSx, minWidth:{ xs:'100%', md:52 } }}>Ends</Typography>
              <GlassDatePicker value={endDate} onChange={setEndDate} disabled={isLocked} />
              <TimeInput value={endTime} onChange={v => { setEndTime(v); setError(null); }} hasError={!!error && (endTime.h===null||endTime.m===null||endTime.s===null)} />
            </Box>

            {error && (
              <Box sx={{ display:'flex', alignItems:'flex-start', gap:'6px', mt:1.5, p:'10px 12px', background:error.includes('verlap')?'rgba(251,191,36,.08)':'rgba(248,113,113,.08)', border:`1px solid ${error.includes('verlap')?'rgba(251,191,36,.25)':'rgba(248,113,113,.2)'}`, borderRadius:'8px' }}>
                <Box sx={{ fontSize:13, flexShrink:0 }}>{error.includes('verlap')?'⚡':'⚠'}</Box>
                <Typography sx={{ fontSize:12, color:error.includes('verlap')?'#fbbf24':'#f87171', lineHeight:1.5 }}>{error}</Typography>
              </Box>
            )}

            {renderStop()}
          </Box>

        </Box>

        {/* Footer */}
        <Box sx={{ display:'flex', gap:1, flexShrink:0, p:{ xs:'12px 16px', md:'14px 22px 18px' }, borderTop:'1px solid var(--divider)' }}>
          {isEdit && onDelete && (
            <Box component="button" onClick={onDelete} sx={{ ...btnSx, flex:'none', px:2, background:'rgba(239,68,68,.08)', borderColor:'rgba(239,68,68,.2)', color:'#f87171', '&:hover':{ background:'rgba(239,68,68,.16)' } }}>
              {t('common.delete')}
            </Box>
          )}
          <Box component="button" onClick={onClose} sx={btnSx}>{t('common.cancel')}</Box>
          <Box component="button" onClick={handleSave} sx={{ ...btnSx, background:'linear-gradient(135deg, var(--accent), var(--accent-light))', borderColor:'var(--accent)', color:'#fff', boxShadow:'0 2px 8px var(--accent-glow)', '&:hover':{ opacity:.9 } }}>
            {t('common.save')}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EventModal;
